import { Component, OnInit, ViewChildren, QueryList } from '@angular/core';
import { UserService, ChurchUnitsDataService, SortService, CampusStake, StakeLeader, StakePosition, IComponentHasChanges, CampusStakeDetailsForUpdate } from '../../shared';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LeaderComponent } from '../leader.component';
import * as Config from '../../shared/tokens/constants.tokens';
import { StakeLeaderFormComponent } from '../leader-form/stake-leader-form.component';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-stake-information',
  templateUrl: './stake-information.component.html',
  styleUrls: ['./stake-information.component.scss']
})
export class StakeInformationComponent extends LeaderComponent implements OnInit, IComponentHasChanges {
  @ViewChildren(StakeLeaderFormComponent) leaderForms: QueryList<StakeLeaderFormComponent>;

  stake: CampusStake;
  details: CampusStakeDetailsForUpdate;
  leaders: StakeLeader[];
  loading = false;
  loadingError = false;
  saving = false;
  saved = false;
  showPassword = false;

  stakeForm: FormGroup;

  constructor(
    protected userService: UserService,
    protected dialog: MatDialog,
    private churchService: ChurchUnitsDataService,
    private sorter: SortService,
    private formBuilder: FormBuilder
  ) {
    super(userService, dialog);
  }

  ngOnInit() {
    super.ngOnInit();

    this.stakeForm = this.formBuilder.group({
      officeLocation: ['', [Validators.maxLength(50)]],
      interviewTimes: ['', [Validators.maxLength(50)]],
      phone: ['', [Validators.pattern(Config.Constants.phoneRegex)]],
      // As of 2018-3-6, the email validator assumed it was a required field.  This let's us around that.
      email: ['', [Validators.maxLength(70), Validators.pattern(Config.Constants.emailRegex)]],
      password: ['', [Validators.required]]
    });

    // TODO if not proxied, load the ward details that the leader is in charge of
    if (this.isStakeLeader()) {
      this.loadStakeDetails(this.getStake());
    }
  }

  /**
   * Indicates whether or not there are unsaved changes on the leader.
   */
  hasUnsavedChanges(): boolean {
    return this.stakeHasUnsavedChanges() || this.getFirstLeaderFormWithUnsavedChanges() != null;
  }

  /**
   * Gets a message letting the user know where their unsaved changes are.
   */
  get unsavedMessage(): string {
    const leaderWithUnsaved = this.getFirstLeaderFormWithUnsavedChanges();
    if (leaderWithUnsaved != null) {
      return `You have unsaved changes to the ${leaderWithUnsaved.leader.position}'s information.  Are you sure you want to continue?`;
    } else if (this.stakeHasUnsavedChanges()) {
      return 'You have unsaved changes to the stake\'s informaion.  Are you sure you want to continue?';
    }

    return null;
  }

  /**
   * Saves the stake information.
   */
  saveStakeInformation() {
    if (this.saving || !this.stakeForm.dirty || !this.stakeForm.valid) {
      return;
    }

    // get our data from the form
    const stake = Object.assign({}, this.details, this.stakeForm.value);

    this.saved = false; // reset this flag

    this.saving = true;
    this.churchService
      .updateStakeInformation(stake)
      .pipe(
        finalize(() => (this.saving = false))
      )
      .subscribe(
        updatedStake => {
          this.handleDetails(updatedStake, true);
          this.saved = true;
        },
        error => {
          this.showError(error);
        }
      );
  }

  /**
   * Loads the stake's information for our user to view and edit.
   */
  private loadStakeDetails(stake: CampusStake) {
    this.stake = stake;
    this.loading = true;
    this.churchService
      .getCampusStakeDetailsForUpdate(stake.campusStakeId)
      .pipe(
        finalize(() => (this.loading = false))
      )
      .subscribe(details => this.handleDetails(details), error => (this.loadingError = true));
  }

  /**
   * When we load the details, we need to reset our form and patch in all the values.
   */
  private handleDetails(stakeDetails: CampusStakeDetailsForUpdate, preserveLeaders = false) {
    this.saved = false;
    this.details = stakeDetails;
    this.populateForm();

    // get our leaders ready for updates as well
    if (!preserveLeaders) {
      this.leaders = [];
      if (this.details) {
        if (this.details.president) {
          this.leaders.push(this.details.president);
        } else {
          this.leaders.push(new StakeLeader(StakePosition.President, this.details.campusStakeId));
        }

        if (this.details.firstCounselor) {
          this.leaders.push(this.details.firstCounselor);
        } else {
          this.leaders.push(new StakeLeader(StakePosition.FirstCounselor, this.details.campusStakeId));
        }

        if (this.details.secondCounselor) {
          this.leaders.push(this.details.secondCounselor);
        } else {
          this.leaders.push(new StakeLeader(StakePosition.SecondCounselor, this.details.campusStakeId));
        }
      }
    }
  }

  /**
   * Populates our form from the stake's details.
   */
  private populateForm() {
    // reset the form
    this.stakeForm.reset();

    // patch in everything else
    this.stakeForm.patchValue(this.details);
    this.stakeForm.get('password').patchValue(this.details.password);
  }

  /**
   * Gets the first leader form that has unsaved changes; null if none have unsaved changes.
   */
  private getFirstLeaderFormWithUnsavedChanges(): StakeLeaderFormComponent {
    const unsaved = this.leaderForms.find(form => form.hasUnsavedChanges());
    return unsaved !== undefined ? unsaved : null;
  }

  /**
   * Whether or not we have unsaved changes in the stake information itself.
   */
  private stakeHasUnsavedChanges() {
    return this.stakeForm.dirty;
  }

  /**
   * Show or hide password
   */
  togglePassoWordVisibilty() {
    if (this.showPassword) {
      this.showPassword = false;
    } else {
      this.showPassword = true;
    }
  }
}
